import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { CompactProjectCard } from "@/components/projects/cards/CompactProjectCard";
import { ProjectsFilterType } from "@/components/projects/ProjectConstants";
import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useListGroups, useListRoleAssignments, useListUserResourcePermissions } from "@/queries/group-queries";
import { useMembers } from "@/queries/organization-queries";
import { useListProjects, useListProjectStates } from "@/queries/project-queries";
import {
    useListAllRunnerSCMIntegrations,
    useListRunnerEnvironmentClasses,
} from "@/queries/runner-configuration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import Fuse from "fuse.js";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { Project } from "gitpod-next-api/gitpod/v1/project_pb";
import { useMemo, type FC } from "react";
import { ProjectCard } from "./cards/ProjectCard";

const searchOptions = {
    includeScore: true,
    keys: [
        "project.metadata.name",
        "project.id",
        {
            name: "contextUrl",
            getFn: (item: { project: PlainMessage<Project> }) =>
                getRepoUrlFromInitializer(item.project.initializer) ?? "",
        },
    ],
};

export const ProjectsList: FC<{
    canEdit?: boolean;
    showRecentlyUsedOnly?: boolean;
    filter?: ProjectsFilterType;
    search?: string;
    selectProjectId?: string;
    selectEnvironmentClass?: string;
    selectRunnerId?: string;
    useCompactLayout?: boolean;

    onCreateSuccess?: () => void;
}> = ({
    canEdit,
    filter,
    search,
    selectProjectId,
    selectEnvironmentClass,
    selectRunnerId,
    useCompactLayout,
    onCreateSuccess,
}) => {
    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const { data: projectData, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectData?.projects.map((p) => p.id),
    );
    const { value: isGroupManagementEnabled } = useFlag(Flags.GroupManagementEnabled, false);
    const { data: resourcePermissions, isPending: isPendingResourcePermissions } = useListUserResourcePermissions();
    const { data: groupsData } = useListGroups();

    // Fetch all role assignments for PROJECT resources once at the list level
    // This is more efficient than fetching per-project in each card
    // Omit resourceId to fetch all assignments for this resource type
    const { data: allProjectRoleAssignments } = useListRoleAssignments({
        resourceType: ResourceType.PROJECT,
        enabled: isGroupManagementEnabled,
    });

    const { data: membersData } = useMembers();

    const allProjects = useMemo(() => {
        if (!projectData || !projectsStates) {
            return []; // should be caught by isLoading check
        }

        return (projectData.projects ?? []).map((project) => {
            return {
                project,
                state: projectsStates.find((p) => p.projectId === project.id),
            };
        });
    }, [projectData, projectsStates]);

    const { data: runnerEnvironmentClasses } = useListRunnerEnvironmentClasses(selectRunnerId);
    const { data: scmIntegrations } = useListAllRunnerSCMIntegrations();

    const filteredByState = useMemo(() => {
        return allProjects.filter((p) => {
            if (selectProjectId) {
                return p.project.id === selectProjectId;
            }
            if (selectEnvironmentClass) {
                if (selectEnvironmentClass === "local") {
                    return !!p.state?.environmentClasses?.find(
                        (ec) => ec.environmentClass.case === "localRunner" && ec.environmentClass.value === true,
                    );
                } else {
                    return !!p.state?.environmentClasses?.find(
                        (ec) =>
                            ec.environmentClass.case === "environmentClassId" &&
                            ec.environmentClass.value === selectEnvironmentClass,
                    );
                }
            }
            if (selectRunnerId) {
                return runnerEnvironmentClasses?.some(
                    (rec) =>
                        !!p.state?.environmentClasses?.find(
                            (ec) =>
                                ec.environmentClass.case === "environmentClassId" &&
                                ec.environmentClass.value === rec.id,
                        ),
                );
            }

            let visible = true;
            if (filter === ProjectsFilterType.Active) {
                // Show if shared org-wide OR shared via groups the user is a member of
                const orgShared = typeof p.state?.shared !== "undefined" ? p.state.shared : false;
                const groupShared = resourcePermissions?.data.get(ResourceType.PROJECT)?.has(p.project.id) ?? false;
                visible = orgShared || groupShared;
            }
            if (filter === ProjectsFilterType.NotShared) {
                // "Not Shared" means not shared org-wide (independent of group sharing)
                visible = typeof p.state?.shared !== "undefined" ? !p.state.shared : false;
            }
            return visible;
        });
    }, [
        allProjects,
        filter,
        selectEnvironmentClass,
        selectProjectId,
        selectRunnerId,
        runnerEnvironmentClasses,
        resourcePermissions,
    ]);

    const projectsSearchIndex = useMemo(() => {
        return Fuse.createIndex(searchOptions.keys, filteredByState);
    }, [filteredByState]);

    const filteredBySearch = useMemo(() => {
        if (!search) {
            return filteredByState;
        }

        const fuse = new Fuse(filteredByState, searchOptions, projectsSearchIndex);
        const result = fuse.search(search);

        return result.map((r) => r.item);
    }, [filteredByState, projectsSearchIndex, search]);

    const isLoading =
        isLoadingProjectsStates ||
        isPendingProjects ||
        isLoadingUser ||
        (isGroupManagementEnabled && isPendingResourcePermissions) ||
        !projectData ||
        !projectsStates ||
        !user;

    if (isLoading) {
        return <LoadingState delay={true} />;
    }

    const noProjects = allProjects.length === 0;
    const noSearchResults = !noProjects && search && filteredBySearch.length === 0;

    return (
        <div className="flex w-full flex-col gap-4 pb-6" data-testid="projects-list">
            <ul
                className={
                    useCompactLayout
                        ? "flex w-full flex-col gap-2"
                        : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
                }
            >
                {filteredBySearch.map(({ project, state }) => {
                    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
                    const scmId =
                        projectContextUrl &&
                        scmIntegrations?.find((int) => int.host === new URL(projectContextUrl).host)?.scmId;

                    // Filter role assignments to only those for this specific project
                    const projectRoleAssignments = allProjectRoleAssignments?.assignments?.filter(
                        (a) => a.resourceId === project.id,
                    );

                    return (
                        <li key={project.id}>
                            {useCompactLayout ? (
                                <CompactProjectCard
                                    project={project}
                                    orgMembers={membersData?.members}
                                    forceHoverState={!!selectProjectId}
                                    onCreateSuccess={onCreateSuccess}
                                />
                            ) : (
                                <ProjectCard
                                    canEdit={useCompactLayout ? false : canEdit ?? false}
                                    project={project}
                                    state={state}
                                    orgMembers={membersData?.members}
                                    currentUserId={user?.id ?? ""}
                                    forceHoverState={!!selectProjectId}
                                    onCreateSuccess={onCreateSuccess}
                                    scmId={scmId}
                                    roleAssignments={projectRoleAssignments}
                                    allGroups={groupsData?.groups}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
            {noProjects && (
                <EmptyStateCard
                    title="No projects"
                    description="Projects are pre-configured templates that provide one-click environments. Only admins can create projects for your organization."
                    data-testid="no-projects-empty-state"
                />
            )}
            {noSearchResults && (
                <EmptyStateCard
                    title="No results found"
                    description="No projects match your search criteria."
                    data-testid="no-search-results-empty-state"
                />
            )}
        </div>
    );
};
